import React, { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { cn, ui } from '../../utils';

const QUICK_REACTIONS = ['❤️', '😂', '🎉', '🤔', '👀', '🔥', '👍', '👎'];

const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  {
    label: 'Smileys',
    emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊', '😇', '🥰', '😍', '🤩', '😘', '😜', '🤪', '😎', '🤓', '🥳', '😢', '😭', '😤', '🤯', '😱', '🫡'],
  },
  {
    label: 'Gestures',
    emojis: ['👍', '👎', '👏', '🙌', '🤝', '🤞', '✌️', '🤟', '🫶', '💪', '👋', '🫡', '🙏', '✋', '🤙'],
  },
  {
    label: 'Hearts',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💯', '💔', '❤️‍🔥', '💕', '💖', '💗'],
  },
  {
    label: 'Objects',
    emojis: ['🎉', '🎊', '🏆', '🥇', '⭐', '🌟', '💡', '🔥', '💎', '🚀', '⚡', '🎯', '🎨', '🎵', '📌', '✅', '❌', '⚠️', '💬', '👀', '🤔', '💤', '🍿', '🍕', '☕', '🍻'],
  },
];

interface EmojiReactionPickerProps {
  onSelect: (emoji: string) => void;
  userReactions?: string[];
}

export function EmojiReactionPicker({ onSelect, userReactions = [] }: EmojiReactionPickerProps) {
  const [showFull, setShowFull] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showFull) return;
    const handleClick = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowFull(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showFull]);

  return (
    <div className="relative" ref={pickerRef}>
      <div className={cn('flex items-center gap-0.5 rounded-xl border border-border bg-card p-1 shadow-2xl shadow-black/30')}>
        {QUICK_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(emoji);
            }}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg text-sm transition-colors duration-150 hover:bg-background',
              userReactions.includes(emoji) && 'bg-blue-500/15 ring-1 ring-blue-500/30',
            )}
            title={emoji}
          >
            {emoji}
          </button>
        ))}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setShowFull((open) => !open);
          }}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-150 hover:bg-background hover:text-foreground',
            showFull && 'bg-background text-foreground',
          )}
          title="More reactions"
          aria-label="More reactions"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      {showFull ? (
        <div className="absolute bottom-full right-0 z-50 mb-1 max-h-64 w-72 overflow-y-auto rounded-[1.25rem] border border-border bg-card p-2 shadow-2xl shadow-black/30">
          {EMOJI_CATEGORIES.map((category) => (
            <div key={category.label} className="mb-2 last:mb-0">
              <div className="mb-1 px-1 text-xs font-medium text-muted-foreground">{category.label}</div>
              <div className="flex flex-wrap gap-0.5">
                {category.emojis.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(emoji);
                      setShowFull(false);
                    }}
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-lg text-lg transition-colors duration-150 hover:bg-background',
                      userReactions.includes(emoji) && 'bg-blue-500/15 ring-1 ring-blue-500/30',
                    )}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface ReactionPillsProps {
  counts: Record<string, number>;
  userReactions: string[];
  reactors?: Record<string, string[]>;
  onToggle: (emoji: string) => void;
}

export function ReactionPills({ counts, userReactions, reactors = {}, onToggle }: ReactionPillsProps) {
  const entries = Object.entries(counts).filter(([, count]) => count > 0);
  if (entries.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 px-1">
      {entries.map(([emoji, count]) => {
        const isOwn = userReactions.includes(emoji);
        const names = reactors[emoji] || [];
        return (
          <button
            key={emoji}
            type="button"
            onClick={() => onToggle(emoji)}
            className={cn(
              'inline-flex min-h-[30px] cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors duration-150 hover:shadow-sm',
              isOwn
                ? 'border-blue-500/40 bg-blue-500/10 text-blue-300'
                : 'border-border bg-card/80 text-muted-foreground hover:border-blue-500/30 hover:bg-background',
            )}
            title={names.length > 0 ? names.join(', ') : undefined}
          >
            <span className="text-sm leading-none">{emoji}</span>
            <span className="font-medium">{count}</span>
          </button>
        );
      })}
    </div>
  );
}
